import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { Col, Row } from "antd";
import Title from "antd/lib/typography/Title";
import { debounce } from "helpers/functions";
import useViewport from "components/UseViewport/UseViewport";
import { Event } from "interfaces/events";
import colors from "constants/colors";
import EventCard from "./EventCard/EventCard";
import {
  Carousel,
  CarouselLastButton,
  CarouselNextButton,
  CarouselWrapper,
  MainContent,
  StyledFirstWave,
  StyledSecondWave,
  WaveContainer,
} from "./style";
import HeroMobile from "./HeroMobile/HeroMobile";

const HeroSection: React.FC = () => {
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>();
  const [bgOffset, setBgOffset] = useState(0);
  const [showRightSideButton, setShowRightSideButton] = useState(false);
  const [showLeftSideButton, setShowLeftSideButton] = useState(false);

  const carouselRef = useRef<HTMLDivElement | null>(null);

  const { width } = useViewport();
  const breakpoint = 768;

  useEffect(() => {
    axios
      .get<Event[]>("/events/")
      // is_visible = 1 => visible on web page
      // is_visible = 3 => visible everywhere
      .then(response => setUpcomingEvents(response.data?.filter(e => e.is_visible === 1 || e.is_visible === 3)));
  }, []);

  const checkIfScrolledToLeftEnd = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel || !upcomingEvents) return;
    const firstChild = carousel.children[0];
    const hasScrolledToEnd = carousel.getBoundingClientRect().left - firstChild.getBoundingClientRect().left < 50;
    setShowLeftSideButton(!hasScrolledToEnd);
  }, [upcomingEvents]);

  const checkIfScrolledToRightEnd = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel || !upcomingEvents) return;
    const lastChild = carousel.children[carousel.children.length - 1];
    const hasScrolledToEnd = lastChild.getBoundingClientRect().right - carousel.getBoundingClientRect().right < 50;
    setShowRightSideButton(!hasScrolledToEnd);
  }, [upcomingEvents]);

  useEffect(() => {
    const handleSideControls = debounce(() => {
      checkIfScrolledToRightEnd();
    }, 500);
    handleSideControls();
    window.addEventListener("resize", handleSideControls);
    return () => {
      window.removeEventListener("resize", handleSideControls);
    };
  }, [checkIfScrolledToRightEnd, upcomingEvents]);

  const scrollBackward = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    const carouselChildren = Array.prototype.slice.call(Array.from(carousel.children));
    const reversed = carouselChildren.reverse();
    const childToScrollTo = reversed.find(
      child => child.getBoundingClientRect().left < carousel.getBoundingClientRect().left
    );
    if (!childToScrollTo) return;
    const scrollAmount = childToScrollTo.getBoundingClientRect().right - carousel.getBoundingClientRect().right; // denne linja
    setBgOffset(prev => prev + scrollAmount / 5);
    setTimeout(() => {
      carousel?.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }, 100);
  }, [setBgOffset]);

  const scrollForward = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    const childToScrollTo = Array.from(carousel.children).find(
      child => child.getBoundingClientRect().right > carousel.getBoundingClientRect().right
    );
    if (!childToScrollTo) return;
    const scrollAmount = childToScrollTo.getBoundingClientRect().left - carousel.getBoundingClientRect().left;
    setBgOffset(prev => prev + scrollAmount / 5);
    setTimeout(() => {
      carousel?.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }, 100);
  }, [setBgOffset]);

  return (
    <>
      <div style={{ position: "relative", overflowX: "hidden", overflowY: "visible" }}>
        <MainContent bgOffset={bgOffset}>
          <Col
            style={{
              padding:
                width < breakpoint
                  ? "7.5vh max(calc(1em + 7vw), 10px)" // Mobile padding
                  : "7.5vh max(calc(2em + 7vw), 10px)", // Default padding
              width: "100%",
              margin: "auto",
            }}
          >
            <Row justify="start">
              <Col>
                <Title style={{ color: "white", fontSize: "min(7vh, 8vw)" }}>
                  Sammen
                  <br />
                  om{" "}
                  <span
                    style={{
                      textDecoration: "underline",
                      textDecorationColor: colors.secondary,
                    }}
                  >
                    drømmejobben
                  </span>
                </Title>
              </Col>
            </Row>
            {width < breakpoint ? (
              // Render HeroMobile for mobile screens
              <HeroMobile upcomingEvents={upcomingEvents} />
            ) : (
              // Render the desktop carousel for larger screens
              <CarouselWrapper
                style={{ marginTop: "5em" }}
                showRightSideControls={showRightSideButton}
                showLeftSideControls={showLeftSideButton}
              >
                <Carousel
                  gutter={30}
                  wrap={false}
                  ref={carouselRef}
                  onScroll={debounce(() => {
                    checkIfScrolledToLeftEnd();
                    checkIfScrolledToRightEnd();
                  }, 100)}
                  showRightSideControls={showRightSideButton}
                  showLeftSideControls={showLeftSideButton}
                >
                  {upcomingEvents?.length === 0 ? (
                    <Title italic style={{ color: "white", fontSize: "1.5em" }}>
                      Ingen arrangementer i nærmeste fremtid...
                    </Title>
                  ) : (
                    upcomingEvents?.map(event => <EventCard key={event.id} event={event} />)
                  )}
                </Carousel>
                <CarouselLastButton style={{ left: 0, opacity: showLeftSideButton ? 1 : 0 }} onClick={scrollBackward}>
                  <ArrowLeftOutlined />
                </CarouselLastButton>
                <CarouselNextButton style={{ right: 0, opacity: showRightSideButton ? 1 : 0 }} onClick={scrollForward}>
                  <ArrowRightOutlined />
                </CarouselNextButton>
              </CarouselWrapper>
            )}
          </Col>
        </MainContent>
      </div>
      <WaveContainer>
        <StyledSecondWave
          preserveAspectRatio="none"
          viewBox="0 0 1437 150"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 111.57V0H1437V148.174C1410.13 150.068 1380.6 150.445 1348 149C1108.66 138.387 965.007 80.8965 889.9 50.8379L889.876 50.8262C862.382 39.8262 844.076 32.5 833.625 32.5C816.22 32.5 801.199 27.2305 788.237 18.0469C757.161 16.0273 701.604 12.2539 653.563 8.99219C605.714 5.74414 565.322 3 564 3C498.769 3 426.049 34.7871 351.076 67.5586C243.302 114.668 130.874 163.812 29.3462 123.494C20.0649 119.809 10.2549 115.818 0 111.57Z"
            fill="#D98618"
          />
        </StyledSecondWave>
        <StyledFirstWave
          preserveAspectRatio="none"
          viewBox="0 0 1437 111"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 0V81C111.059 111.492 176.729 92.0566 262.861 66.5645C330.676 46.4922 411.175 22.668 536.5 16.5C649.731 10.9277 760.27 35.5312 874.672 60.9961C1046.09 99.1504 1226.18 139.236 1437 82.6348V0H0Z"
            fill="#0A283F"
          />
        </StyledFirstWave>
      </WaveContainer>
    </>
  );
};

export default HeroSection;
